import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowUpRight, Check, Copy} from '../../icons';
import {copyText} from '../../agency/prompts/copyText';

const COPIED_MS = 2500;

/**
 * Los dos botones de un link de instalación listo (`copy.md` §2): «Instalar en Shopify» lo abre en una
 * pestaña nueva (lo instala quien administre la tienda) y «Copiar link para tu cliente» lo copia y confirma
 * «Link copiado» al lado, sin cambiar el rótulo del botón. Es el reemplazo nombrado de «Mandar link al
 * cliente». Si el navegador no deja copiar, el link aparece en un campo para copiarlo a mano.
 *
 * `copy` es para los tests.
 */
export function ShopifyLinkActions({link, copy = copyText}) {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    if (!link) return null;

    const onCopy = () => {
        copy(link).then(() => {
            setCopyFailed(false);
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), COPIED_MS);
        }, () => {
            setCopied(false);
            setCopyFailed(true);
        });
    };

    return (
        <div className="shl-actions">
            <div className="shl-actions__buttons">
                <a className="shl-btn -primary" href={link} target="_blank" rel="noopener noreferrer"
                   data-testid="shopify-link-install">
                    {t('shopify_link.requests.install')}
                    <ArrowUpRight size={14} aria-hidden="true"/>
                </a>
                <button type="button" className={`shl-btn${copied ? ' -done' : ''}`} onClick={onCopy}
                        data-testid="shopify-link-copy">
                    {copied ? <Check size={14} aria-hidden="true"/> : <Copy size={14} aria-hidden="true"/>}
                    {t('shopify_link.requests.copy')}
                </button>
                {/* «Link copiado» se anuncia acá: el rótulo del botón no cambia, así no salta de ancho. */}
                <span className="shl-actions__copied" role="status" aria-live="polite">
                    {copied ? t('shopify_link.requests.copied') : ''}
                </span>
            </div>
            {copyFailed && (
                <div className="shl-actions__manual">
                    <span className="shl-actions__error" role="alert">{t('shopify_link.copy_failed')}</span>
                    <input type="text" className="form-control shl-actions__link" readOnly value={link}
                           aria-label={t('shopify_link.requests.copy')}
                           onFocus={(event) => event.target.select()}/>
                </div>
            )}
        </div>
    );
}

export default ShopifyLinkActions;
